/**
 * SaveGrantButton — inline save toggle for a grant card / detail page.
 *
 * Free-tier 4th-save behaviour (G1 dispatch, non-negotiable):
 * - When the backend returns 402 `save_cap_reached`, the button
 *   morphs IN-PLACE into a pill linking to `/pricing`. No modal, no
 *   blocking overlay, no other UI change.
 * - Sibling controls on the page remain fully interactive.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { BookmarkPlus, BookmarkCheck, Star } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { toast } from "sonner";

const UPGRADE_COPY = "Upgrade to Member to save more grants";

export default function SaveGrantButton({
  grantId,
  initialSaved = false,
  onChange,
  testIdPrefix = "grant-save",
  size = "md",
}) {
  const [saved, setSaved] = useState(initialSaved);
  const [capReached, setCapReached] = useState(false);
  const [busy, setBusy] = useState(false);

  async function toggle() {
    if (busy) return;
    setBusy(true);
    try {
      if (saved) {
        await api.delete(`/grants/${grantId}/save`);
        setSaved(false);
        if (onChange) onChange({ saved: false });
      } else {
        const res = await api.post(`/grants/${grantId}/save`);
        setSaved(true);
        setCapReached(false);
        if (onChange) onChange({ saved: true, count: res?.data?.count });
      }
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.detail?.code;
      if (status === 402 && code === "save_cap_reached") {
        // Inline morph — button becomes the upgrade pill.
        setCapReached(true);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setBusy(false);
    }
  }

  const padding = size === "lg" ? "px-5 py-2.5 text-sm" : "px-4 py-2 text-sm";

  if (capReached) {
    return (
      <Link
        to="/pricing"
        data-testid={`${testIdPrefix}-upgrade-cta`}
        className={`inline-flex items-center gap-2 rounded-full font-semibold transition-all duration-300 ${padding}`}
        style={{
          background: "rgba(167,139,250,0.12)",
          border: "1px solid rgba(167,139,250,0.55)",
          color: "#5D50C8",
        }}
      >
        <Star className="w-4 h-4" />
        {UPGRADE_COPY}
      </Link>
    );
  }

  if (saved) {
    return (
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        data-testid={`${testIdPrefix}-remove`}
        className={`inline-flex items-center gap-2 rounded-full font-semibold transition-all duration-300 disabled:opacity-60 ${padding}`}
        style={{
          background: "rgba(52,211,153,0.14)",
          border: "1px solid rgba(52,211,153,0.45)",
          color: "#0F9F70",
        }}
      >
        <BookmarkCheck className="w-4 h-4" />
        Saved
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={`${testIdPrefix}-add`}
      className={`inline-flex items-center gap-2 rounded-full font-semibold text-v2-background bg-v2-accent hover:bg-white transition-all duration-300 disabled:opacity-60 ${padding}`}
    >
      <BookmarkPlus className="w-4 h-4" />
      {busy ? "Saving…" : "Save this grant"}
    </button>
  );
}
